import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { recipeCopy } from 'js/src/agent/skills/recipeCopy';
import type { Skill } from '../types';

export const createListAndStepsVideoSkill: Skill = {
    name: SKILL_NAMES.createListAndStepsVideo,
    description:
        'Clearly separated chapters, each opening on its own title scene, closing on a summary of every point and a call to action. Use for listicles, step-by-step guides, how-tos, tutorials, and FAQs.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        ...recipeCopy(SKILL_NAMES.createListAndStepsVideo),
        preview: 'list-and-steps',
        frames: [
            'list-and-steps-1',
            'list-and-steps-2',
            'list-and-steps-3',
            'list-and-steps-4',
            'list-and-steps-5',
        ],
    },
    get instructions(): string {
        return `# Create List and Steps Video
For a list of items, a step-by-step guide, or a FAQ, told as a small number
of clearly separated chapters — Point A title → Point A content → Point B
title → Point B content → Point C title → Point C content — closing with a
summary of every point and a call to action.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- The list content — the items, steps, or FAQ content itself, or a source to
  pull it from. If they've only named a topic with no concrete list yet,
  research and propose one rather than asking them to write it themselves
- Closing call to action
- Ideal duration
- Any specific media that needs to appear for one or more points
- Voice (search according to below instructions to offer good options). Set
  \`require_voiceover: true\` on that question — each point is introduced by the
  narration, so "No voiceover" isn't on offer.
- Unless specified, ask which points to cover. Multi-select list of options for them to pick from.

Show users options of templates matching the subject's tone. Search if none of the
available ones seem an obvious fit.

## Narration and music
- **Voice: calm and patient.** Search with \`${TOOL_NAMES.searchVoices}\` for a tone that
  reads as calm, warm, and unhurried — pass "patient"/"calm" through the
  \`search\` free-text field alongside whatever \`tone\` values the tool
  recognizes, and pick from the results.
- **Music: subtle and non-distracting.** Search \`${TOOL_NAMES.searchMusic}\` for minimal,
  ambient, low-key background tracks — nothing busy, percussive, or
  attention-grabbing. Once applied, set the music volume low (roughly
  15-25) with \`${TOOL_NAMES.setMusicVolume}\` so it never competes with the narration or
  undercuts the calm, patient read.
- **Never use em dashes (—)** in the script or in on-screen text. Write the
  two halves as separate sentences, or join with a comma or conjunction.
- Well-placed pauses give the narration room to breathe — a beat before a
  new point starts, ahead of the closing summary. Sparingly, not between
  every sentence.

## Script
- Tone is **informative, communicative, educational, and instructional**:
  the video should teach the viewer something and walk them through it in a
  clear, logical order — plainspoken and direct, not hype-y ad copy.
- **On-screen text is display copy drawn from the spoken line, not a
  transcript of it.** The narration carries the sentence; the screen lands
  its key phrase, number, or payoff — roughly 2-8 words. If the spoken line
  is "2, cost: most teams spend a third of their budget on licenses," the
  text is "2. Cost" on the chapter title and "A third of the budget on
  licenses" on the content scene, not the full sentence typed out under the
  voice. Full sentences on screen are the exception, at most about one scene
  in four; let the media carry the rest.
- **Display copy never goes beyond the narration.** Every claim and number
  on screen must be one the narration makes over that scene and must agree
  with it (the voice says "a third," the screen never says 40%). Don't bolt
  on extra commentary or facts that were never spoken.
- **Only use a scene's secondary text asset (subtitle, body copy) when the
  scene genuinely has a second point from the narration to land** — don't
  populate it just to avoid leaving it blank; a secondary asset with nothing
  from the narration to put there should stay empty.
- Build the narrative spine first — the chapters and their order — before
  exact wording, so one changed chapter doesn't force a full rewrite.
- **List and count fidelity.** If the narration states a count ("5 steps,"
  "3 reasons"), the video must show exactly that many chapters, matching the
  list content gathered in Setup. If narration references step numbers, the
  design must actually display that number — spoken-only numbers are a
  defect.
- **Use numerals for each point, not spelled-out ordinals.** Write "1," "2,"
  "3" rather than "first," "second," "third" — in the narration itself (say
  "1, get your documents ready" rather than "first, get your documents
  ready") and in every on-screen number this recipe displays. This applies
  throughout: the script, each chapter's title text, and the closing
  summary.
- Write final wording budgeted to the target duration from Setup — chapters
  and word count fit the duration, not the other way around.

## Structure — chapters
- Turn the gathered list content into chapters: each item, step, or
  question-and-answer pair from Setup becomes one chapter.
- **Every chapter opens with its own title-category scene** naming that
  specific point (Point A's title, then Point B's title, and so on) — title
  scenes are not reserved for the very start of the video in this format;
  each new point gets one. Lead that title with its numeral (1, 2, 3 — not
  "first," "second") if the design has room for one, either worked into the
  title text itself or in a dedicated number asset if the design provides
  one. For the very first chapter, fold a brief mention of the overall topic
  into that title scene alongside the point itself (e.g. a subtitle line),
  so the viewer gets context without a separate, redundant intro title
  scene ahead of it.
- After each chapter's title scene, build one or more content scenes that
  cover that point — however many it actually takes. A short point gets one
  content scene; a longer one gets two or three, each holding one idea and
  sized to what's actually spoken over it (see Script and Asset Timing above
  and below).
- **Close with a real summary of every point**, then the call to action from
  Setup. The summary must actually recap each chapter's point by name, not a
  generic "in summary..." line, before handing off to the CTA — the CTA is
  never optional in this format.

## Scene design variety
- **Use a variety of scene designs across the video.** Actively rotate
  across categories — media, collage, text, quote, lists, data/chart,
  animated captions, icon designs — rather than leaning on two or three; a
  title-category scene opens every chapter as described above, on top of
  that variety.
- Never the same design on two consecutive scenes.
- **Match the design to the content**: does each text asset comfortably fit
  what you're putting in it (shape-bounded assets — circles, badges, pills —
  hold one or two words, no more)? Does the asset count match what the scene
  has to say? A design missing a needed asset is the wrong design.
- **A Quote design is for an actual attributed quotation** — someone's words,
  with a real name to credit them to. A list item, a step, or a strong
  summary line is not a quote. The decorative quotation mark is usually a
  read-only asset you cannot remove, so blanking the name/role slots leaves
  it stranded over copy nobody said. No speaker to credit means a text
  design, not a quote design. Most quote designs supply the quotation mark
  themselves, so don't add quote characters unless the design's own
  placeholder text already has them.
- List-like content within a chapter gets a listicle/list design whose asset
  count fits the item count.
- **Avoid back-to-back text-only scenes** — after a text scene, the next one
  should carry media.
- **Caption-style designs** (ANIMATED_CAPTIONS category) are a good
  deliberate choice for a scene with no strong media — not a default, and
  not never.

## Asset timing — anchor every point precisely
- **Anchor each chapter's title text to the exact words in the narration
  where that point is introduced** (\`${TOOL_NAMES.addVoiceoverScene}\` with \`words\` for
  that phrase) — the title appearing exactly on cue is what makes "a new
  point is starting" register visually.
- Apply the same word-level anchoring inside each chapter's content scenes:
  the on-screen text is drawn from the words its scene's \`words\` range
  covers — their key phrase or figure, not a transcript of the span, and
  never unspoken text or a point from elsewhere in the script.
- **Size each scene's word-span to a natural, readable chunk — not a
  fragment, and not a run-on.** A word-bound scene's on-screen time is set
  by how long those words take to say, so the words you choose for \`words\`
  directly control how long the text stays up: too few words (two or three)
  and the text flashes by before it can be read even though it's perfectly
  synced; too many and the scene runs long or feels cramped. Split at
  natural clause or sentence boundaries — a comma, a conjunction, a full
  stop. As a rough feel: a span spoken in much under two seconds is
  probably too short and belongs merged with its neighbor; a span running
  past four or five seconds should be split at a natural break. This is the
  same target as \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\` check.
- Bias toward more, shorter scenes over fewer, longer ones — but "shorter"
  means cutting at the next natural clause boundary, never shaving words off
  a scene's span to hit a shorter scene count. The right pace feels brisk
  but readable, never a flicker and never a drag.

## Media
- Load \`${SKILL_NAMES.findUserMedia}\` before going through the user's own library,
  \`${SKILL_NAMES.findStockMedia}\` before your first stock search, and \`${SKILL_NAMES.findDamMedia}\`
  too when a DAM is connected. If the user named specific media in Setup,
  use it for the point(s) it belongs to instead of searching.
- Reframe within the asset so the subject is well-composed and nothing
  important sits under text (\`${SKILL_NAMES.adjustMedia}\` skill).
- Per point, a stat worth seeing becomes a chart rather than just being stated.

## Styling
- Colors and styling come from the template or the user's brand kit — apply
  a kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **Turn captions off** (\`${TOOL_NAMES.setCaptions}\` with \`enabled: false\`) unless the
  user specifically asked for them on.
- The chapter count and content match the source list/steps/FAQ exactly —
  nothing added, nothing dropped, and any spoken count matches what's shown.
- Points are numbered with numerals (1, 2, 3) throughout — in the spoken
  script and on screen — not spelled out as "first," "second," "third."
- Every chapter opens with its own title scene naming that specific point,
  timed to appear exactly when that point is introduced in the narration —
  none were skipped, and none doubled up back-to-back.
- Each chapter's content scene count fits how much there was to say for
  that point — not one overstuffed scene, not artificially fragmented.
- **Spot-check several scenes against their transcript span**: does the
  displayed text reinforce what's spoken during that scene's \`words\`
  range — its key phrase or figure — without transcribing it, and without
  contradicting it or adding claims (often in a secondary asset) the
  narration never makes? If \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\` flags a scene as too short
  for its text, check whether that's too much stacked into one scene (split
  it) or unspoken filler text (remove it) — don't just extend the duration.
- Check the other end of that same problem: is any scene flashing by too
  fast to read even though its text fits its spoken words? That means its
  scene's \`words\` span was cut too short — merge it with its neighbor at
  the nearest natural clause boundary.
- The voice reads as calm and patient, and the music stays subtle and low
  under the narration rather than competing with it.
- The closing summary actually names every point covered, and the video
  ends on the exact call to action the user asked for.
- Captions are off unless the user asked for them on.`;
    },
};
